/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { Button, Table, Input } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { DeleteRole } from "~/components/Role/DeleteRole";
import FlexContainer from "~/components/Styled/FlexContainer";
import { ACTIONS, Can } from "~/routing";
import { useAxios } from "~/tools";
import { Values } from "./types";

type InitialValues = {
  id: string;
  name: string;
};

type InlineEditRoleProps = {
  initialValues: InitialValues;
  refetchRoles: () => void;
  fetchTeam: () => void;
};

function InlineEditRole({
  initialValues,
  refetchRoles,
  fetchTeam,
}: InlineEditRoleProps) {
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const onSubmit = async (values: Values) => {
    try {
      await Axios().put(`/roles/${initialValues?.id}`, values);
      setIsEditMode(false);
      refetchRoles();
      fetchTeam();
    } catch (e) {
      showError(e.response);
    }
  };

  const { register, handleSubmit } = useForm();
  const [isEditMode, setIsEditMode] = React.useState(false);

  const EditForm = (
    <Table.tr>
      <Table.td css={{ width: "100%" }}>
        <Input
          type="text"
          ref={register}
          small
          name="name"
          defaultValue={initialValues.name || ""}
        />
      </Table.td>
      <Table.td>
        <FlexContainer>
          <Button
            onClick={handleSubmit(onSubmit)}
            type="button"
            size="sm"
            severity="medium"
            icon="save"
          />
          <Button
            onClick={() => setIsEditMode(false)}
            css={{ marginLeft: "0.5rem" }}
            type="button"
            size="sm"
            severity="medium"
            icon="close"
          />
        </FlexContainer>
      </Table.td>
    </Table.tr>
  );
  const DisplayText = (
    <Table.tr>
      <Table.td css={{ width: "100%" }}>{initialValues.name}</Table.td>
      <Table.td>
        <FlexContainer justify="center">
          <Button
            onClick={() => setIsEditMode(true)}
            type="button"
            size="sm"
            severity="medium"
            icon="edit"
          />
          <Can action={ACTIONS.DELETE_ROLE}>
            <DeleteRole
              fetchTeam={fetchTeam}
              deleteCallback={refetchRoles}
              role={initialValues}
            />
          </Can>
        </FlexContainer>
      </Table.td>
    </Table.tr>
  );

  if (isEditMode) {
    return EditForm;
  }

  return <>{DisplayText}</>;
}

export { InlineEditRole };
